import Alpine from 'alpinejs';
import axios from '../../config/axios.js';
import Konva from 'konva';

function money(n) {
  return 'Rp ' + Number(n || 0).toLocaleString('id-ID');
}

function uid() {
  return crypto.randomUUID?.() || `l_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function createSurangEditor() {
  return {
    loading: true,
    saving: false,
    exporting: false,
    sending: false,
    projectId: '',
    project: null,
    scene: null,
    selectedId: null,
    tool: 'select',
    zoom: 1,
    dirty: false,
    history: [],
    historyIndex: -1,
    stage: null,
    layer: null,
    transformer: null,
    nodes: {},
    autosaveTimer: null,
    guideBleed: true,
    stageReady: false,
    panelTab: 'elements',
    elementCategory: 'shapes',
    libraryElements: [],
    libraryLoading: false,
    elementsMeta: { categories: [] },
    inspector: {
      text: '',
      fontSize: 36,
      fontFamily: 'Inter',
      fill: '#0f172a',
      width: 320,
    },
    fontOptions: [
      { value: 'Montserrat', label: 'Montserrat' },
      { value: 'Inter', label: 'Inter' },
      { value: 'Manrope', label: 'Manrope' },
      { value: 'Plus Jakarta Sans', label: 'Plus Jakarta Sans' },
      { value: 'Arial', label: 'Arial' },
      { value: 'Georgia', label: 'Georgia' },
      { value: 'Verdana', label: 'Verdana' },
      { value: 'Impact', label: 'Impact' },
      { value: 'Times New Roman', label: 'Times New Roman' },
    ],

    get selectedLayer() {
      return (this.scene?.layers || []).find((l) => l.id === this.selectedId) || null;
    },

    get editableLayers() {
      return (this.scene?.layers || []).filter((l) => l.type !== 'background').slice().reverse();
    },

    async init() {
      if (!Alpine.store('auth')?.isLoggedIn) {
        window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
        return;
      }
      this.projectId = this.$el.dataset.projectId || '';
      await this.load();
      await this.ensureFonts();
      await this.loadEditorAssets();
      this.$watch('loading', (v) => {
        if (!v && this.project) this.scheduleMountStage();
      });
      this.$watch('elementCategory', () => this.loadLibraryElements());
      this.scheduleMountStage();
      window.addEventListener('beforeunload', (e) => {
        if (this.dirty) {
          e.preventDefault();
          e.returnValue = '';
        }
      });
    },

    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/api/v1/surang/projects/${this.projectId}`);
        this.project = data.data;
        this.scene = structuredClone(data.data.scene_json || { layers: [] });
        if (!Array.isArray(this.scene.layers)) {
          this.scene.layers = [];
        }
        this.pushHistory(true);
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal memuat proyek.', 'error');
      } finally {
        this.loading = false;
      }
    },

    async ensureFonts() {
      if (!document.fonts?.load) return;
      const families = this.fontOptions.map((f) => f.value);
      await Promise.all(
        families.map((family) => document.fonts.load(`400 16px "${family}"`).catch(() => null)),
      );
    },

    canvasFontFamily(name) {
      const n = String(name || 'Inter').trim();
      return n.includes(',') ? n.split(',')[0].trim().replace(/['"]/g, '') : n;
    },

    applyTextToNode(node, meta) {
      const text = String(meta.text ?? '');
      node.text(text.length ? text : ' ');
      node.fontSize(Math.max(8, Number(meta.fontSize) || 36));
      node.fontFamily(this.canvasFontFamily(meta.fontFamily));
      node.fill(meta.fill || '#0f172a');
      node.width(Math.max(40, Number(meta.width) || 320));
      node.wrap('word');
      node.align(meta.align || 'left');
      node.height(undefined);
    },

    syncInspectorFromSelection() {
      const meta = this.selectedLayer;
      if (!meta || meta.type !== 'text') return;
      this.inspector = {
        text: meta.text ?? '',
        fontSize: Number(meta.fontSize) || 36,
        fontFamily: this.canvasFontFamily(meta.fontFamily || 'Inter'),
        fill: meta.fill || '#0f172a',
        width: Number(meta.width) || 320,
      };
    },

    applyTextInspector() {
      const meta = this.selectedLayer;
      if (!meta || meta.type !== 'text') return;
      const node = this.nodes[meta.id];
      if (!node) return;

      meta.text = this.inspector.text ?? '';
      meta.fontSize = Math.max(8, Number(this.inspector.fontSize) || 36);
      meta.fontFamily = this.inspector.fontFamily || 'Inter';
      meta.fill = this.inspector.fill || '#0f172a';
      meta.width = Math.max(40, Number(this.inspector.width) || 320);

      this.applyTextToNode(node, meta);
      this.layer?.batchDraw();
      this.markDirty();
    },

    async loadEditorAssets() {
      try {
        const [metaRes, fontRes] = await Promise.all([
          axios.get('/api/v1/surang/elements/meta'),
          axios.get('/api/v1/surang/fonts'),
        ]);
        this.elementsMeta = metaRes.data?.data || { categories: [] };
        const cats = this.elementsMeta.categories || [];
        if (cats.length && !cats.includes(this.elementCategory)) {
          this.elementCategory = cats[0];
        }
        await this.registerCustomFonts(fontRes.data?.data?.fonts || []);
        await this.loadLibraryElements();
      } catch {
        /* optional assets */
      }
    },

    async registerCustomFonts(fonts) {
      if (!fonts?.length || !document.fonts) return;
      for (const f of fonts) {
        if (!f.url || !f.family) continue;
        try {
          const face = new FontFace(f.family, `url(${f.url})`);
          await face.load();
          document.fonts.add(face);
          if (!this.fontOptions.some((o) => o.value === f.family)) {
            this.fontOptions.push({ value: f.family, label: `${f.name}${f.access === 'pro' ? ' · Pro' : ''}` });
          }
        } catch {
          /* skip broken font files */
        }
      }
    },

    async loadLibraryElements() {
      this.libraryLoading = true;
      try {
        const { data } = await axios.get('/api/v1/surang/elements', {
          params: { category: this.elementCategory },
        });
        this.libraryElements = data.data?.elements || [];
      } catch {
        this.libraryElements = [];
      } finally {
        this.libraryLoading = false;
      }
    },

    setPanelTab(tab) {
      this.panelTab = tab;
      if (tab === 'elements') this.loadLibraryElements();
    },

    scheduleMountStage() {
      this.stageReady = false;
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.mountStage();
          if (!this.stage) {
            setTimeout(() => this.mountStage(), 120);
          }
          this.stageReady = !!this.stage;
        });
      });
    },

    insertLibraryElement(el) {
      if (!el?.url) return;
      if (!this.stageReady || !this.layer) {
        window.toast?.('Artboard belum siap. Tunggu sebentar…', 'warning');
        this.scheduleMountStage();
        return;
      }
      const mime = String(el.mime_type || '');
      if (mime.startsWith('video/') || mime.startsWith('audio/')) {
        window.toast?.('Video/audio belum didukung di artboard. Gunakan gambar.', 'info');
        return;
      }
      this.addLayer({
        id: uid(),
        type: 'image',
        name: el.name || 'Elemen',
        src: el.url,
        x: 80,
        y: 80,
        width: 240,
        height: 240,
        locked: false,
        visible: true,
      });
    },

    ensureStageReady() {
      if (!this.stage || !this.layer) {
        this.scheduleMountStage();
        return false;
      }
      return true;
    },

    zoomPercent() {
      return Math.round((this.zoom || 1) * 100);
    },

    applyZoom(nextZoom) {
      if (!this.stage || !this.scene) return;
      const w = this.scene.width_px || 800;
      const h = this.scene.height_px || 500;
      const z = Math.max(0.12, Math.min(2, Number(nextZoom) || 1));
      this.zoom = z;
      this.stage.width(Math.round(w * z));
      this.stage.height(Math.round(h * z));
      this.stage.scale({ x: z, y: z });
      this.stage.batchDraw();
    },

    zoomIn() {
      this.applyZoom(this.zoom + 0.1);
    },

    zoomOut() {
      this.applyZoom(this.zoom - 0.1);
    },

    zoomFit() {
      const host = document.getElementById('surang-konva');
      if (!host || !this.scene) return;
      const w = this.scene.width_px || 800;
      const h = this.scene.height_px || 500;
      const fit = Math.min(1, (host.parentElement?.clientWidth - 48 || 720) / w, 720 / h);
      this.applyZoom(Math.max(0.12, fit));
    },

    mountStage() {
      const host = document.getElementById('surang-konva');
      if (!host || !this.scene) return;
      host.innerHTML = '';
      const w = this.scene.width_px || 800;
      const h = this.scene.height_px || 500;
      const fit = Math.min(1, (host.parentElement?.clientWidth - 48 || 720) / w, 720 / h);
      this.zoom = Math.max(0.12, fit);

      this.stage = new Konva.Stage({
        container: 'surang-konva',
        width: Math.round(w * this.zoom),
        height: Math.round(h * this.zoom),
        scaleX: this.zoom,
        scaleY: this.zoom,
      });

      this.layer = new Konva.Layer();
      this.stage.add(this.layer);
      this.transformer = new Konva.Transformer({
        rotateEnabled: true,
        keepRatio: false,
        enabledAnchors: [
          'top-left',
          'top-right',
          'bottom-left',
          'bottom-right',
          'middle-left',
          'middle-right',
        ],
        anchorSize: 10,
        borderStroke: '#B22222',
        anchorStroke: '#B22222',
        anchorFill: '#ffffff',
        boundBoxFunc: (oldBox, newBox) => (newBox.width < 24 || newBox.height < 16 ? oldBox : newBox),
      });
      this.layer.add(this.transformer);

      this.stage.on('click tap', (e) => {
        if (e.target === this.stage) {
          this.select(null);
          return;
        }
        const id = e.target.getAttr('surangId');
        if (id) this.select(id);
      });

      this.stage.on('dblclick dbltap', (e) => {
        const id = e.target.getAttr('surangId');
        const meta = (this.scene.layers || []).find((l) => l.id === id);
        if (meta?.type === 'text') this.startInlineTextEdit(meta.id);
      });

      this.rebuildNodes();
      this.drawGuides();
      this.stageReady = true;
    },

    rebuildNodes() {
      if (!this.layer) return;
      Object.values(this.nodes).forEach((n) => n.destroy());
      this.nodes = {};
      (this.scene.layers || []).forEach((meta) => {
        const node = this.createNode(meta);
        if (!node) return;
        this.nodes[meta.id] = node;
        this.layer.add(node);
      });
      this.transformer?.moveToTop();
      this.layer.draw();
      if (this.selectedId) this.select(this.selectedId);
    },

    createNode(meta) {
      let node = null;
      if (meta.type === 'background') {
        node = new Konva.Rect({
          x: 0,
          y: 0,
          width: this.scene.width_px,
          height: this.scene.height_px,
          fill: meta.fill || '#ffffff',
          listening: false,
          name: 'bg',
        });
      } else if (meta.type === 'rect') {
        node = new Konva.Rect({
          x: meta.x || 40,
          y: meta.y || 40,
          width: meta.width || 160,
          height: meta.height || 100,
          fill: meta.fill || '#B22222',
          stroke: meta.stroke || null,
          strokeWidth: meta.strokeWidth || 0,
          rotation: meta.rotation || 0,
          draggable: !meta.locked,
          opacity: meta.visible === false ? 0.2 : 1,
        });
      } else if (meta.type === 'circle') {
        node = new Konva.Circle({
          x: (meta.x || 100) + (meta.radius || 50),
          y: (meta.y || 100) + (meta.radius || 50),
          radius: meta.radius || 50,
          fill: meta.fill || '#2563eb',
          rotation: meta.rotation || 0,
          draggable: !meta.locked,
        });
      } else if (meta.type === 'text') {
        node = new Konva.Text({
          x: meta.x || 60,
          y: meta.y || 60,
          text: String(meta.text ?? '').length ? String(meta.text) : ' ',
          fontSize: Math.max(8, meta.fontSize || 36),
          fontFamily: this.canvasFontFamily(meta.fontFamily || 'Inter'),
          fill: meta.fill || '#0f172a',
          width: Math.max(40, meta.width || 320),
          wrap: 'word',
          align: meta.align || 'left',
          draggable: !meta.locked,
          rotation: meta.rotation || 0,
          visible: meta.visible !== false,
        });
      } else if (meta.type === 'image' && meta.src) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        node = new Konva.Image({
          x: meta.x || 40,
          y: meta.y || 40,
          width: meta.width || 200,
          height: meta.height || 200,
          draggable: !meta.locked,
          rotation: meta.rotation || 0,
          image: undefined,
        });
        img.onload = () => {
          node.image(img);
          this.layer.batchDraw();
        };
        img.src = meta.src;
      } else {
        return null;
      }

      node.setAttr('surangId', meta.id);
      node.on('dragend', () => {
        this.syncMetaFromNode(meta.id);
        this.markDirty();
      });
      node.on('transform', () => {
        if (meta.type !== 'text') return;
        this.liveScaleText(meta.id);
      });
      node.on('transformend', () => {
        this.syncMetaFromNode(meta.id);
        this.syncInspectorFromSelection();
        this.markDirty();
      });
      return node;
    },

    liveScaleText(id) {
      const node = this.nodes[id];
      const meta = (this.scene.layers || []).find((l) => l.id === id);
      if (!node || !meta || meta.type !== 'text') return;

      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      const absScaleX = Math.abs(scaleX);
      const absScaleY = Math.abs(scaleY);

      // Corner drag ≈ uniform scale → font size; side drag → box width
      const isSideStretch = Math.abs(absScaleX - absScaleY) > 0.08;
      if (isSideStretch) {
        meta.width = Math.max(40, node.width() * absScaleX);
        node.width(meta.width);
        node.scaleX(1);
        node.scaleY(1);
      } else {
        const nextSize = Math.max(8, Math.min(200, Math.round((meta.fontSize || node.fontSize()) * absScaleY)));
        meta.fontSize = nextSize;
        meta.width = Math.max(40, node.width() * absScaleX);
        node.fontSize(nextSize);
        node.width(meta.width);
        node.scaleX(1);
        node.scaleY(1);
      }
      this.inspector.fontSize = meta.fontSize;
      this.inspector.width = meta.width;
    },

    startInlineTextEdit(id) {
      const node = this.nodes[id];
      const meta = (this.scene.layers || []).find((l) => l.id === id);
      if (!node || !meta || meta.type !== 'text' || !this.stage) return;

      const stageBox = this.stage.container().getBoundingClientRect();
      const abs = node.getAbsolutePosition();
      const area = document.createElement('textarea');
      area.value = meta.text || '';
      area.style.position = 'fixed';
      area.style.left = `${stageBox.left + abs.x * this.zoom}px`;
      area.style.top = `${stageBox.top + abs.y * this.zoom}px`;
      area.style.width = `${Math.max(120, (meta.width || 320) * this.zoom)}px`;
      area.style.minHeight = `${Math.max(36, (meta.fontSize || 42) * this.zoom * 1.4)}px`;
      area.style.fontSize = `${(meta.fontSize || 42) * this.zoom}px`;
      area.style.fontFamily = this.canvasFontFamily(meta.fontFamily || 'Inter');
      area.style.color = meta.fill || '#0f172a';
      area.style.border = '2px solid #7c3aed';
      area.style.padding = '4px 6px';
      area.style.zIndex = '9999';
      area.style.background = 'rgba(255,255,255,0.96)';
      area.style.lineHeight = '1.2';
      area.style.resize = 'both';
      document.body.appendChild(area);
      area.focus();
      area.select();

      const commit = () => {
        meta.text = area.value;
        this.inspector.text = area.value;
        this.applyTextToNode(node, meta);
        this.layer?.batchDraw();
        this.markDirty();
        area.remove();
      };
      area.addEventListener('blur', commit);
      area.addEventListener('keydown', (ev) => {
        if (ev.key === 'Escape') {
          area.remove();
        }
      });
    },

    drawGuides() {
      if (!this.layer || !this.guideBleed) return;
      const bleedPx = ((this.scene.bleed_mm || 3) / (this.scene.width_mm || 1)) * (this.scene.width_px || 1);
      const safePx = ((this.scene.safe_mm || 5) / (this.scene.width_mm || 1)) * (this.scene.width_px || 1);
      const w = this.scene.width_px;
      const h = this.scene.height_px;
      const bleed = new Konva.Rect({
        x: bleedPx,
        y: bleedPx,
        width: Math.max(0, w - bleedPx * 2),
        height: Math.max(0, h - bleedPx * 2),
        stroke: '#ef4444',
        strokeWidth: 1,
        dash: [6, 4],
        listening: false,
        name: 'guide',
      });
      const safe = new Konva.Rect({
        x: safePx,
        y: safePx,
        width: Math.max(0, w - safePx * 2),
        height: Math.max(0, h - safePx * 2),
        stroke: '#22c55e',
        strokeWidth: 1,
        dash: [4, 4],
        listening: false,
        name: 'guide',
      });
      this.layer.find('.guide').forEach((n) => n.destroy());
      this.layer.add(bleed);
      this.layer.add(safe);
      bleed.moveToTop();
      safe.moveToTop();
      this.transformer?.moveToTop();
      this.layer.draw();
    },

    select(id) {
      this.selectedId = id;
      if (!this.transformer) return;
      if (!id || !this.nodes[id]) {
        this.transformer.nodes([]);
        this.layer?.draw();
        return;
      }
      const meta = (this.scene.layers || []).find((l) => l.id === id);
      if (meta?.locked || meta?.type === 'background') {
        this.transformer.nodes([]);
      } else {
        this.transformer.nodes([this.nodes[id]]);
      }
      this.syncInspectorFromSelection();
      this.layer.draw();
    },

    syncMetaFromNode(id) {
      const node = this.nodes[id];
      const meta = (this.scene.layers || []).find((l) => l.id === id);
      if (!node || !meta) return;
      if (meta.type === 'circle') {
        meta.radius = node.radius();
        meta.x = node.x() - node.radius();
        meta.y = node.y() - node.radius();
      } else {
        meta.x = node.x();
        meta.y = node.y();
        if (typeof node.width === 'function') meta.width = node.width() * (node.scaleX?.() || 1);
        if (typeof node.height === 'function') meta.height = node.height() * (node.scaleY?.() || 1);
        if (node.scaleX) {
          node.width(meta.width);
          node.height(meta.height);
          node.scaleX(1);
          node.scaleY(1);
        }
      }
      meta.rotation = node.rotation();
      if (meta.type === 'text') {
        const scaleX = Math.abs(node.scaleX?.() || 1);
        const scaleY = Math.abs(node.scaleY?.() || 1);
        if (Math.abs(scaleX - 1) > 0.01 || Math.abs(scaleY - 1) > 0.01) {
          const isSideStretch = Math.abs(scaleX - scaleY) > 0.08;
          if (!isSideStretch) {
            meta.fontSize = Math.max(8, Math.min(200, Math.round((node.fontSize() || 36) * scaleY)));
            node.fontSize(meta.fontSize);
          }
          meta.width = Math.max(40, node.width() * scaleX);
          node.width(meta.width);
          node.scaleX(1);
          node.scaleY(1);
        }
        const raw = String(node.text() ?? '');
        meta.text = raw === ' ' ? '' : raw;
        meta.fontSize = node.fontSize();
        meta.fontFamily = this.canvasFontFamily(node.fontFamily());
        meta.fill = node.fill();
        meta.width = node.width();
        if (this.selectedId === id) this.syncInspectorFromSelection();
      }
    },

    syncAll() {
      (this.scene.layers || []).forEach((l) => {
        if (l.type !== 'background') this.syncMetaFromNode(l.id);
      });
    },

    markDirty() {
      this.dirty = true;
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = setTimeout(() => this.saveScene(), 1800);
    },

    pushHistory(reset = false) {
      if (reset) {
        this.history = [structuredClone(this.scene)];
        this.historyIndex = 0;
        return;
      }
      this.history = this.history.slice(0, this.historyIndex + 1);
      this.history.push(structuredClone(this.scene));
      if (this.history.length > 40) this.history.shift();
      this.historyIndex = this.history.length - 1;
    },

    undo() {
      if (this.historyIndex <= 0) return;
      this.historyIndex -= 1;
      this.scene = structuredClone(this.history[this.historyIndex]);
      this.rebuildNodes();
      this.drawGuides();
      this.markDirty();
    },

    redo() {
      if (this.historyIndex >= this.history.length - 1) return;
      this.historyIndex += 1;
      this.scene = structuredClone(this.history[this.historyIndex]);
      this.rebuildNodes();
      this.drawGuides();
      this.markDirty();
    },

    addText() {
      if (!this.ensureStageReady()) return;
      this.addLayer({
        id: uid(),
        type: 'text',
        name: 'Teks',
        text: 'Add a heading',
        x: 80,
        y: 80,
        width: 360,
        fontSize: 48,
        fontFamily: 'Inter',
        fill: '#0f172a',
        align: 'left',
        locked: false,
        visible: true,
      });
    },

    addRect() {
      if (!this.ensureStageReady()) return;
      this.addLayer({
        id: uid(),
        type: 'rect',
        name: 'Kotak',
        x: 100,
        y: 100,
        width: 180,
        height: 120,
        fill: '#B22222',
        locked: false,
        visible: true,
      });
    },

    addCircle() {
      if (!this.ensureStageReady()) return;
      this.addLayer({
        id: uid(),
        type: 'circle',
        name: 'Lingkaran',
        x: 120,
        y: 120,
        radius: 60,
        fill: '#2563eb',
        locked: false,
        visible: true,
      });
    },

    addLayer(meta) {
      if (!Array.isArray(this.scene.layers)) {
        this.scene.layers = [];
      }
      this.scene.layers.push(meta);
      this.pushHistory();
      this.rebuildNodes();
      this.drawGuides();
      this.select(meta.id);
      this.markDirty();
    },

    async uploadImage(e) {
      const file = e.target.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('project_id', this.projectId);
      try {
        const { data } = await axios.post('/api/v1/surang/assets', fd);
        const url = data.data?.url;
        if (!url) throw new Error('no url');
        this.addLayer({
          id: uid(),
          type: 'image',
          name: file.name || 'Gambar',
          src: url,
          x: 60,
          y: 60,
          width: 240,
          height: 240,
          locked: false,
          visible: true,
        });
      } catch (err) {
        window.toast?.(err.response?.data?.message || 'Gagal upload gambar.', 'error');
      } finally {
        e.target.value = '';
      }
    },

    deleteSelected() {
      const meta = this.selectedLayer;
      if (!meta || meta.type === 'background' || meta.locked) return;
      this.scene.layers = this.scene.layers.filter((l) => l.id !== meta.id);
      this.selectedId = null;
      this.pushHistory();
      this.rebuildNodes();
      this.drawGuides();
      this.markDirty();
    },

    bringForward() {
      const i = this.scene.layers.findIndex((l) => l.id === this.selectedId);
      if (i < 0 || i >= this.scene.layers.length - 1) return;
      const tmp = this.scene.layers[i];
      this.scene.layers[i] = this.scene.layers[i + 1];
      this.scene.layers[i + 1] = tmp;
      this.pushHistory();
      this.rebuildNodes();
      this.drawGuides();
      this.markDirty();
    },

    sendBackward() {
      const i = this.scene.layers.findIndex((l) => l.id === this.selectedId);
      if (i <= 1) return; // keep above background
      const tmp = this.scene.layers[i];
      this.scene.layers[i] = this.scene.layers[i - 1];
      this.scene.layers[i - 1] = tmp;
      this.pushHistory();
      this.rebuildNodes();
      this.drawGuides();
      this.markDirty();
    },

    applyInspector() {
      const meta = this.selectedLayer;
      if (!meta) return;
      if (meta.type === 'text') {
        this.applyTextInspector();
        return;
      }
      const node = this.nodes[meta.id];
      if (!node) return;
      if (meta.type === 'rect' || meta.type === 'background') {
        node.fill(meta.fill || '#ffffff');
        if (meta.type === 'rect') {
          node.width(Number(meta.width) || 100);
          node.height(Number(meta.height) || 100);
        }
      } else if (meta.type === 'circle') {
        node.fill(meta.fill || '#2563eb');
        node.radius(Number(meta.radius) || 50);
      }
      this.layer.draw();
      this.pushHistory();
      this.markDirty();
    },

    async saveScene(createNewVersion = false) {
      if (!this.project || this.project.status === 'sent_to_spk') return;
      this.syncAll();
      this.saving = true;
      try {
        const { data } = await axios.post(`/api/v1/surang/projects/${this.projectId}/scene`, {
          scene_json: this.scene,
          create_new_version: createNewVersion,
        });
        this.dirty = false;
        if (data.data?.project) {
          this.project = { ...this.project, ...data.data.project };
        }
        if (createNewVersion) window.toast?.('Versi baru disimpan.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menyimpan.', 'error');
      } finally {
        this.saving = false;
      }
    },

    async restoreVersion(versionNo) {
      if (!versionNo || this.project?.status === 'sent_to_spk') return;
      this.saving = true;
      try {
        const { data } = await axios.post(`/api/v1/surang/projects/${this.projectId}/restore-version`, {
          version_no: versionNo,
        });
        this.scene = structuredClone(data.data?.scene_json || this.scene);
        if (data.data?.project) this.project = { ...this.project, ...data.data.project };
        this.pushHistory(true);
        this.rebuildNodes();
        this.drawGuides();
        this.dirty = false;
        window.toast?.(data.message || 'Versi dipulihkan.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal pulihkan versi.', 'error');
      } finally {
        this.saving = false;
      }
    },

    async exportAndMaybeSend(send = false) {
      if (!this.stage) return;
      this.syncAll();
      await this.saveScene(false);

      this.exporting = true;
      try {
        // Hide guides for export
        this.layer.find('.guide').forEach((n) => n.hide());
        this.transformer.nodes([]);
        this.layer.draw();

        const fullRes = !!this.project?.can_export_full_res;
        const pixelRatio = fullRes ? Math.min(3, 1 / this.zoom) : Math.min(1.5, 1 / this.zoom);
        let dataUrl = this.stage.toDataURL({ pixelRatio, mimeType: 'image/png' });

        if (!fullRes) {
          dataUrl = await this.applyWatermark(dataUrl);
        }

        const blob = await (await fetch(dataUrl)).blob();
        const fd = new FormData();
        fd.append('preview', blob, 'preview.png');
        fd.append('print', blob, 'print.png');

        const { data } = await axios.post(`/api/v1/surang/projects/${this.projectId}/export`, fd);
        this.project = { ...this.project, ...data.data };
        window.toast?.(data.message || 'Export berhasil.', 'success');

        if (send) await this.sendToSpk();
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal export.', 'error');
      } finally {
        this.layer.find('.guide').forEach((n) => n.show());
        this.drawGuides();
        this.exporting = false;
      }
    },

    applyWatermark(dataUrl) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const c = document.createElement('canvas');
          c.width = img.width;
          c.height = img.height;
          const ctx = c.getContext('2d');
          ctx.drawImage(img, 0, 0);
          ctx.fillStyle = 'rgba(15,23,42,.35)';
          ctx.font = `${Math.max(18, Math.floor(img.width / 28))}px sans-serif`;
          ctx.translate(img.width / 2, img.height / 2);
          ctx.rotate(-Math.PI / 6);
          const text = this.project?.watermark_text || 'USPATIH SURANG — PREVIEW';
          ctx.fillText(text, -img.width / 3, 0);
          resolve(c.toDataURL('image/png'));
        };
        img.src = dataUrl;
      });
    },

    async sendToSpk() {
      this.sending = true;
      try {
        const { data } = await axios.post(`/api/v1/surang/projects/${this.projectId}/send-to-spk`);
        this.project = { ...this.project, ...data.data };
        window.toast?.(data.message || 'SPK draft dibuat.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal kirim SPK.', 'error');
      } finally {
        this.sending = false;
      }
    },

    money,
  };
}

Alpine.data('surangEditor', createSurangEditor);
